import type { ApplicationData, SubmitResult } from '@policygenius/ai-assistant-shared';
import { AiAssistant as AiAssistantComponent } from '@policygenius/ai-assistant-ui';
import React, { useCallback, useContext, useRef } from 'react';

// eslint-disable-next-line import/no-unassigned-import -- compiled Tailwind/global styles from the UI package
import '@policygenius/ai-assistant-ui/styles.css';

import { PaymentMode } from 'containers/App/constants';
import { useUpdateHouseholdInformationMutation } from 'containers/CommonApp/HouseholdInformation/graphql/mutations/HouseholdInformationMutation';
import { useSelectMyQuotesPolicyMutation } from 'containers/MyQuotes/graphql/SelectMyQuotesPolicy';
import { AuthContext } from 'contexts/Auth';
import { isEmail, isPhone, isPresent } from 'forms/validators/utils';
import { useSaveEmailMutation } from 'graphql/mutations/customers/SaveEmail/mutation';
import { useUpdateResidenceZipMutation } from 'graphql/mutations/customers/updateResidenceZip/mutation';
import { useUpdateUserAttributesMutation } from 'graphql/mutations/customers/updateUserAttributes/mutation';
import { usePolicyPreferencesUpdateMutation } from 'graphql/mutations/lifeApplications/policy/policyPreferencesUpdate/mutation';
import { useSubmitLifeApplicationMutation } from 'graphql/mutations/lifeApplications/submitLifeApplication/mutation';
import useSelector from 'store/useSelector';

const API_URL = process.env.LIFE_BFF_BASE_URL || '';

const AiAssistant: React.FC = () => {
  const { auth } = useContext(AuthContext);
  const activeLifeApplicationId = useSelector((app) => app.activeLifeApplicationId);
  const activeUserUuid = useSelector((app) => app.activeUserUuid);
  const [saveIdentityProfile] = useSaveEmailMutation();
  const [updateResidenceZip] = useUpdateResidenceZipMutation();
  const [updateUserAttributes] = useUpdateUserAttributesMutation();
  const [updatePolicyPreferences] = usePolicyPreferencesUpdateMutation();
  const [selectMyQuotesPolicy] = useSelectMyQuotesPolicyMutation();
  const [updateHouseholdInformation] = useUpdateHouseholdInformationMutation();
  const [submitLifeApplication] = useSubmitLifeApplicationMutation();
  const bearerToken = auth.type !== 'none' && auth.token ? auth.token : undefined;

  console.log('activeLifeApplicationId', activeLifeApplicationId);
  console.log('activeUserUuid', activeUserUuid);

  // Guards against re-submitting the same application on a retry (e.g. the
  // bot UI's retry affordance re-invoking handleApplicationReady after a lost
  // response). submit_life_application's own resolver fires a Five9 dialer
  // notification with no tag-based dedupe on the Rails side -- unlike the old
  // submit_common_app -> Life::ConditionalDialer path, which tags the case
  // before dialing -- so re-submitting the same application would re-fire the
  // dialer campaign for a lead that was already handed off. Only cleared on a
  // genuine failure, so a real retry after an error can still proceed.
  const hasSubmittedApplicationRef = useRef(false);

  const handleApplicationReady = useCallback(
    async (data: ApplicationData): Promise<SubmitResult> => {
      const { profile } = data;
      // A selected quote, an opt-out, and a no-carriers-match all end in a
      // submit_life_application call below (that's the only mutation that
      // advances internal_case.lifecycle_stage to application_requested and
      // triggers the welcome email / dialer entry) -- so all three require
      // the same up-front contact-info validation and idempotency guard.
      const isApplicationSubmission =
        Boolean(data.selectedQuote) ||
        data.noMatchReason === 'opt_out' ||
        data.noMatchReason === 'no_carriers';
      // ai-assistant-ui's ContactForm always hands back phone formatted as
      // "(555) 123-4567" (masked as the user types), never raw digits. Digits
      // only, everywhere phone is used below -- both isPhone's 10-digit check
      // and submit_life_application's `phone: PhoneNumber!` scalar (schema.json
      // describes it as "10-digit phone number") reject punctuation.
      const normalizedPhone = profile.phone?.replace(/\D/g, '');

      if (isApplicationSubmission) {
        if (hasSubmittedApplicationRef.current) {
          return { success: true, applicationId: activeLifeApplicationId };
        }

        // Validated up front, before any mutation runs -- email/first_name/
        // last_name/phone are required (non-nullable) on
        // submit_life_application, but optional on ApplicationProfile, so the
        // bot can in principle hand off a profile that never collected
        // contact info. Fail fast with a clear message instead of letting
        // saveIdentityProfile/updateResidenceZip/updatePolicyPreferences/
        // selectMyQuotesPolicy commit server-side first and only then
        // discovering submit_life_application can't run -- that would leave
        // the case with a policy selected and applied but never advanced to
        // application_requested, a new variant of the "stuck lead" bug this
        // change exists to fix.
        const contactInfoError = [
          isEmail(profile.email),
          isPresent(profile.firstName),
          isPresent(profile.lastName),
          isPhone(normalizedPhone),
        ]
          .filter(Boolean)
          .join(' ');

        if (contactInfoError) {
          return { success: false, error: contactInfoError };
        }

        hasSubmittedApplicationRef.current = true;
      }

      // Submits the application for real -- this is the one call in this
      // flow that advances internal_case.lifecycle_stage from
      // application_not_started to application_requested (via
      // complete_application! -> complete_life_application! in Rails), and
      // it does this regardless of whether a quote was selected: the
      // resolver only skips creating an applied policy record when there's
      // no selected quote, it still advances the lifecycle stage and fires
      // the dialer/welcome-email notification either way. submit_common_app
      // (the prior call here) does NOT do this -- its resolver only
      // schedules a delayed dialer callback and has zero effect on
      // lifecycle_stage, which is why AI-assisted leads (including opt-outs
      // and no-carriers-match leads) were getting stuck at
      // application_not_started. Do not call both: submit_life_application's
      // own resolver already triggers an equivalent (non-delayed) dialer
      // notification via ApplicationUpdateService.trigger_change, so keeping
      // submit_common_app alongside this would double-fire the phone dialer
      // campaign for every AI-assisted lead.
      const submitApplication = async (internalCaseTags?: string[]): Promise<string> => {
        const response = await submitLifeApplication({
          variables: {
            input: {
              customer_id: activeUserUuid,
              email: profile.email as string,
              first_name: profile.firstName as string,
              last_name: profile.lastName as string,
              phone: normalizedPhone as string,
              ...(internalCaseTags ? { internal_case_tags: internalCaseTags } : {}),
            },
          },
        });

        const submittedApplication =
          response.data?.life_applications?.submit_life_application?.life_application;

        // The mutation can resolve without a GraphQL error yet still return
        // life_application: null (a valid Maybe<LifeApplication> per the
        // schema) if Rails couldn't complete the application for this
        // customer_id. Treat that the same as a thrown error rather than
        // reporting success for an application that was never actually
        // submitted.
        if (!submittedApplication) {
          throw new Error(
            'submit_life_application did not return a life application -- the application may not have been submitted.'
          );
        }

        return submittedApplication.id;
      };

      try {
        // gender/dob are folded into this same identity_profile_update call
        // (rather than UpdateBasicInfo's separate one) because they're only
        // ever collected as part of the quote-inputs step of the
        // conversation -- an opt-out can happen before the bot asks for
        // them. IdentityProfileUpdateMutationInput treats every field as
        // optional, so omitting gender/date_of_birth here is a normal
        // partial update, not a rejected one.
        await saveIdentityProfile({
          variables: {
            input: {
              customer_id: activeUserUuid,
              email: profile.email,
              first_name: profile.firstName,
              last_name: profile.lastName,
              phone: normalizedPhone,
              ...(profile.gender ? { gender: profile.gender } : {}),
              ...(profile.dob ? { date_of_birth: profile.dob } : {}),
            },
          },
        });

        // Split out from UpdateBasicInfo (used by the standard funnel form,
        // where gender/dob/zip are all guaranteed present by form
        // validation): that mutation declares $dateOfBirth/$gender as
        // non-null operation variables, so reusing it here would mean
        // either sending a null it rejects, or silently dropping the zip
        // update and the life_started_flow flag below along with it just
        // because gender/dob happen to be missing. Each piece of profile
        // data the bot actually collected gets persisted independently
        // instead.
        if (profile.zipCode) {
          await updateResidenceZip({
            variables: {
              input: {
                customer_id: activeUserUuid,
                zip_code: profile.zipCode,
              },
            },
          });
        }

        await updateUserAttributes({
          variables: {
            input: {
              uuid: activeUserUuid,
              key: 'life_started_flow',
              value_json: 'true',
            },
          },
        });

        await updatePolicyPreferences({
          variables: {
            input: {
              customer_id: activeUserUuid,
              individual_income: profile.annualIncome,
            },
          },
        });

        if (profile.maritalStatus) {
          await updateHouseholdInformation({
            variables: {
              householdInput: {
                customer_id: activeUserUuid,
                marital_status: profile.maritalStatus,
              },
              formInput: {
                form_name: 'household_profile',
              },
            },
          });
        }

        let submittedApplicationId = activeLifeApplicationId;

        if (data.selectedQuote) {
          const { selectedQuote } = data;

          await selectMyQuotesPolicy({
            variables: {
              policyInput: {
                application_id: activeLifeApplicationId,
                triggered_from: 'AI Assistant',
                carrier_slug: selectedQuote.carrier,
                carrier_name: selectedQuote.carrierName,
                coverage_amount: selectedQuote.coverageAmount,
                payment_mode: PaymentMode.MONTHLY,
                annual_premium: selectedQuote.annualPremium,
                monthly_premium: selectedQuote.monthlyPremium,
                issue_type: selectedQuote.issueType,
                product_urn: selectedQuote.productUrn,
                policy_name: selectedQuote.productName,
                term_in_years: selectedQuote.termYears,
                underwriting_class: selectedQuote.healthCategory,
                select_and_apply: true,
                table_rating: selectedQuote.tableRating.toString(),
                product_type: selectedQuote.productType,
                plan_identifier_name: selectedQuote.planIdentifierName,
                tobacco: selectedQuote.isTobacco,
                underwriting_class_group: selectedQuote.underwritingClass,
                issue_age: selectedQuote.issueAge,
              },
              preferencesInput: {
                customer_id: activeUserUuid,
                coverage_amount: selectedQuote.coverageAmount,
                term: String(selectedQuote.termYears),
              },
              caseTagsInput: {
                application_id: activeLifeApplicationId,
                internal_case_tags: ['AI-assisted Lead'],
              },
            },
          });

          // internal_case_tags is omitted here: selectMyQuotesPolicy just
          // above already tags this case 'AI-assisted Lead' via
          // caseTagsInput, so sending it again would double-apply the tag.
          submittedApplicationId = await submitApplication();
        } else if (data.noMatchReason === 'opt_out') {
          submittedApplicationId = await submitApplication([
            'AI-assisted Lead',
            'AI Flow- No Estimates',
            'AI Opt-Out Flow',
          ]);
        } else if (data.noMatchReason === 'no_carriers') {
          submittedApplicationId = await submitApplication([
            'AI-assisted Lead',
            'AI Flow- No Estimates',
          ]);
        }

        return { success: true, applicationId: submittedApplicationId };
      } catch (err: unknown) {
        if (isApplicationSubmission) {
          // Only a successful submission should permanently block
          // re-submission -- allow a genuine retry after a real failure.
          // eslint-disable-next-line require-atomic-updates -- intentional ref mutation, not a plain variable susceptible to a silent lost update
          hasSubmittedApplicationRef.current = false;
        }

        const message = err instanceof Error ? err.message : String(err);

        return { success: false, error: message };
      }
    },
    [
      activeLifeApplicationId,
      activeUserUuid,
      saveIdentityProfile,
      updateResidenceZip,
      updateUserAttributes,
      updatePolicyPreferences,
      selectMyQuotesPolicy,
      updateHouseholdInformation,
      submitLifeApplication,
    ]
  );

  return (
    <AiAssistantComponent
      apiUrl={API_URL}
      bearerToken={bearerToken}
      onApplicationReady={handleApplicationReady}
    />
  );
};

export default AiAssistant;
